import { onCLS, onFCP, onINP, onLCP, type Metric } from 'web-vitals';
import type { MetricsCollector } from './metrics';

export interface WebVitalsReporter {
  report(metric: Metric): void;
}

const noopReporter: WebVitalsReporter = {
  report(): void {
    // intentionally a no-op when observability is disabled
  },
};

export interface StartWebVitalsOptions {
  reporter?: WebVitalsReporter;
  /** When true, also `console.debug` each metric. */
  debug?: boolean;
  /**
   * Optional metrics collector. When provided, FCP / LCP / INP / CLS values
   * are forwarded to the collector so the unified report has them.
   */
  metrics?: MetricsCollector;
}

/**
 * Wire up web-vitals to a reporter. Defaults to console.debug so M0 has
 * something visible in DevTools. Cloudflare Web Analytics handles the
 * server-side collection; this is the in-page fallback for FCP/LCP/INP/CLS.
 */
export function startWebVitals(options: StartWebVitalsOptions = {}): void {
  const reporter = options.reporter ?? noopReporter;
  const debug = options.debug ?? true;
  const metrics = options.metrics;

  const send = (metric: Metric): void => {
    if (debug) {
      console.debug(`[web-vitals] ${metric.name}=${metric.value.toFixed(1)} (id=${metric.id})`);
    }
    reporter.report(metric);

    if (metrics) {
      switch (metric.name) {
        case 'FCP':
          metrics.recordFcp(metric.value);
          break;
        case 'LCP':
          metrics.recordLcp(metric.value);
          break;
        case 'INP':
          metrics.recordInp(metric.value);
          break;
        case 'CLS':
          metrics.recordCls(metric.value);
          break;
      }
    }
  };

  onFCP(send);
  onCLS(send);
  onINP(send);
  onLCP(send);
}
